// Portions adapted from AnalyseTool (Apache-2.0), (c) Nikolai Davydov. Modified by apibim.
//
// Two routes to the host, one interface over them.
//
// Commands go out over loopback HTTP whenever the host offers it (`window.__fmBridge`, injected
// before this page's scripts run), because WebView2's message channel has a permanent one-way
// failure: the moment Revit silently upgrades a family on open, nothing this page posts ever reaches
// the host again for the rest of the session — no error, no console entry, the promise simply never
// settles. A loopback `fetch` is the one channel measured to survive that, since it leaves the
// renderer through the network stack instead of WebView2's internal IPC. Over HTTP the reply rides
// the response, so no id correlation is needed for the result.
//
// The message channel remains as the fallback when the host could not open a port, and remains the
// only way the host pushes anything unsolicited — Events and Progress. That direction never breaks.
// On that route WebView2 gives us one undifferentiated `postMessage` pipe each way, so every call
// carries a unique id and the host echoes it back; that id is what lets concurrent commands share
// the pipe, and it is still sent over HTTP so in-flight Progress can find its caller.
//
// Changes from the original: wrapped in the Transport interface, capability discovery via
// GetHostInfo, AbortSignal support, and a bounded id sequence instead of Date.now().

import {
  CommandUnavailableError,
  TransportError,
  type InvokeOptions,
  type ProgressInfo,
  type Transport,
  type TransportCaps,
} from "./types";

interface HostMessage {
  Type: "Response" | "Progress" | "Event";
  Command: string;
  Payload: unknown;
  Id?: string;
  Error?: string | null;
}

interface PendingCall {
  resolve: (value: never) => void;
  reject: (reason: unknown) => void;
  onProgress?: (progress: ProgressInfo) => void;
  timeout: ReturnType<typeof setTimeout>;
}

/**
 * WebView2's postMessage pipe has an observed failure mode where a reply is silently never
 * delivered — no exception on either side, nothing in the WebView's own console, the host-side
 * command completes and sends its reply, and the awaiting promise here just never settles. Without
 * a ceiling, that turns into exactly what a "frozen plugin" report looks like: the SPA parks on
 * "Loading..." forever and the whole panel reads as hung, even though nothing on the Revit side
 * actually deadlocked. 60s is generous enough for the slowest legitimate call (tessellating a
 * complex family) while still turning a silent, unrecoverable freeze into a catchable error the UI
 * can show and move on from.
 */
const CALL_TIMEOUT_MS = 60_000;

interface WebViewBridge {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: { data: HostMessage }) => void): void;
}

export function getWebViewBridge(): WebViewBridge | null {
  const chrome = (globalThis as { chrome?: { webview?: WebViewBridge } }).chrome;
  return chrome?.webview ?? null;
}

/**
 * Where the host will accept commands over loopback HTTP, injected by the host before any of this
 * page's own scripts run. Its absence is not an error — see `sendOverHttp` below.
 */
interface LoopbackBridge {
  url: string;
  tokenHeader: string;
  token: string;
}

function getLoopbackBridge(): LoopbackBridge | null {
  const bridge = (globalThis as { __fmBridge?: LoopbackBridge }).__fmBridge;
  return bridge?.url && bridge.token ? bridge : null;
}

/** A command that the host reports as unknown is a capability gap, not a failure — the UI hides the
 * feature rather than showing an error. Both routes decide that the same way. */
function hostError(command: string, error: string): Error {
  return error.includes("is not registered")
    ? new CommandUnavailableError(command, "revit")
    : new TransportError(command, error);
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export class RevitTransport implements Transport {
  readonly kind = "revit" as const;

  private readonly bridge: WebViewBridge;
  private readonly pending = new Map<string, PendingCall>();

  /** Progress callbacks for calls made over loopback HTTP, which keep no entry in `pending`. */
  private readonly progressHandlers = new Map<string, (progress: ProgressInfo) => void>();

  private sequence = 0;
  private listening = false;
  private initPromise: Promise<void> | null = null;

  private capsState: TransportCaps = { revit: true };
  get caps(): TransportCaps {
    return this.capsState;
  }

  constructor(bridge: WebViewBridge) {
    this.bridge = bridge;
  }

  init(): Promise<void> {
    // Memoised: several components mount at once and each wants capabilities before rendering.
    this.initPromise ??= this.loadHostInfo();
    return this.initPromise;
  }

  private async loadHostInfo(): Promise<void> {
    try {
      const info = await this.invoke<{
        revitYear: number;
        unitSystem?: string;
      }>("GetHostInfo");

      this.capsState = {
        revit: true,
        revitYear: info?.revitYear,
        unitSystem: info?.unitSystem ?? undefined,
      };
    } catch {
      // A host that cannot answer GetHostInfo is still a host — we just do not know its year, so
      // version-compatibility badges degrade to "unknown" rather than the app refusing to start.
      this.capsState = { revit: true };
    }
  }

  invoke<T = unknown>(command: string, payload: unknown = null, options?: InvokeOptions): Promise<T> {
    // Still needed on both routes: Events and Progress only ever arrive over the message channel.
    this.ensureListening();

    if (options?.signal?.aborted) {
      return Promise.reject(new DOMException("Aborted", "AbortError"));
    }

    const id = `fm-${++this.sequence}`;

    // Plain-clone the payload before it leaves. Payloads are built from reactive state, so they can
    // carry Proxies; postMessage serialises via structured clone, which drops such a message
    // silently (no throw, never delivered), and JSON.stringify on the HTTP route would throw on one.
    // Round-tripping through JSON strips the proxies to plain data either way.
    let safePayload: unknown;
    try {
      safePayload = payload == null ? payload : JSON.parse(JSON.stringify(payload));
    } catch (e) {
      return Promise.reject(new TransportError(command, `Payload could not be serialised: ${describe(e)}`));
    }

    const loopback = getLoopbackBridge();
    return loopback
      ? this.sendOverHttp<T>(loopback, command, safePayload, id, options)
      : this.sendOverMessageChannel<T>(command, safePayload, id, options);
  }

  /**
   * The primary route. The host's message channel has a permanent failure mode — after Revit
   * silently upgrades a family on open, nothing this page posts ever reaches the host again for the
   * rest of the session — and a loopback `fetch` is the one channel measured to survive it, because
   * it leaves the renderer through the network stack rather than WebView2's internal IPC.
   *
   * The reply rides the HTTP response, so no id correlation is needed for the result. The id is
   * still sent, because Progress for a call in flight can only come back over the message channel.
   */
  private async sendOverHttp<T>(
    bridge: LoopbackBridge,
    command: string,
    payload: unknown,
    id: string,
    options?: InvokeOptions,
  ): Promise<T> {
    const timeoutMs = options?.timeoutMs ?? CALL_TIMEOUT_MS;
    const controller = new AbortController();
    let timedOut = false;

    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    // Aborting the fetch really does free the socket, but the host command keeps running to
    // completion — same as on the message channel, and for the same reason: there is no cancellation
    // channel into Revit. Commands that must be cancellable take a token in their payload.
    const onAbort = () => controller.abort();
    options?.signal?.addEventListener("abort", onAbort, { once: true });

    if (options?.onProgress) this.progressHandlers.set(id, options.onProgress);

    try {
      const response = await fetch(bridge.url, {
        method: "POST",
        cache: "no-store",
        headers: {
          // The charset is spelled out even though JSON is UTF-8 by definition: without it the
          // host's HttpListener reports the machine's ANSI code page as the request encoding, and
          // anything relying on that reads every non-ASCII path and name as mush.
          "Content-Type": "application/json; charset=utf-8",
          [bridge.tokenHeader]: bridge.token,
        },
        body: JSON.stringify({ Type: "Request", Command: command, Payload: payload, Id: id }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new TransportError(command, `Revit refused the request (HTTP ${response.status}).`);
      }

      const message = (await response.json()) as HostMessage;
      if (message.Error) throw hostError(command, message.Error);
      return message.Payload as T;
    } catch (e) {
      if (timedOut) throw new TransportError(command, `No response from Revit within ${timeoutMs / 1000}s.`);
      if (e instanceof TransportError || e instanceof CommandUnavailableError) throw e;
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      throw new TransportError(command, `The request could not be sent: ${describe(e)}`);
    } finally {
      clearTimeout(timeout);
      options?.signal?.removeEventListener("abort", onAbort);
      this.progressHandlers.delete(id);
    }
  }

  /**
   * The fallback, used only when the host could not open its loopback port. Correlated by id over
   * the one undifferentiated postMessage pipe — and vulnerable to the wedge described above.
   */
  private sendOverMessageChannel<T>(
    command: string,
    safePayload: unknown,
    id: string,
    options?: InvokeOptions,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timeoutMs = options?.timeoutMs ?? CALL_TIMEOUT_MS;
      const timeout = setTimeout(() => {
        if (this.pending.delete(id)) {
          reject(new TransportError(command, `No response from Revit within ${timeoutMs / 1000}s.`));
        }
      }, timeoutMs);

      this.pending.set(id, {
        resolve: resolve as PendingCall["resolve"],
        reject,
        onProgress: options?.onProgress,
        timeout,
      });

      // Abort drops our side of the call. The host command keeps running to completion — WebView2
      // has no cancellation channel — so this frees the caller, not the Revit thread. Commands that
      // must be cancellable have to take a token in their payload.
      options?.signal?.addEventListener(
        "abort",
        () => {
          const call = this.pending.get(id);
          if (call) {
            clearTimeout(call.timeout);
            this.pending.delete(id);
            reject(new DOMException("Aborted", "AbortError"));
          }
        },
        { once: true },
      );

      try {
        this.bridge.postMessage({ Type: "Request", Command: command, Payload: safePayload, Id: id });
      } catch (e) {
        clearTimeout(timeout);
        this.pending.delete(id);
        reject(new TransportError(command, `The request could not be sent: ${describe(e)}`));
      }
    });
  }

  private ensureListening(): void {
    if (this.listening) return;
    this.listening = true;

    this.bridge.addEventListener("message", (event) => {
      const message = event.data;
      if (!message) return;

      // Host-initiated broadcast with no request id (e.g. the active document changed).
      // Re-dispatched as a DOM event so any view can subscribe without importing a bus.
      if (message.Type === "Event") {
        window.dispatchEvent(new CustomEvent(`fm:${message.Command}`, { detail: message.Payload }));
        return;
      }

      if (!message.Id) return;

      // Progress notifies but leaves the call in flight — the result still comes, over whichever
      // route carried the request. Checked before `pending`, because a call made over loopback HTTP
      // has no entry there and would otherwise never see its own progress.
      if (message.Type === "Progress") {
        const handler = this.progressHandlers.get(message.Id) ?? this.pending.get(message.Id)?.onProgress;
        handler?.(message.Payload as ProgressInfo);
        return;
      }

      const call = this.pending.get(message.Id);
      if (!call) return;

      this.pending.delete(message.Id);
      clearTimeout(call.timeout);
      if (message.Error) {
        call.reject(hostError(message.Command, message.Error));
      } else {
        call.resolve(message.Payload as never);
      }
    });
  }
}
